import React, {useMemo, useRef, useState} from 'react';
import {connect} from 'react-redux';
import {
  DashboardPieChartLeft,
  DashboardPieChartRight,
} from '../../Overview/DonutCharts';
import Layout from '../Layout';
import * as Actions from '../../../../store/actions';
import {useFlowOverview} from '../hooks/useOverview';
import FlowOverviewReport from './FlowOverviewReport';
import CardSummary from 'components/CardSummary';
import FilterV2 from 'components/FilterV2';
import {SecondaryButton} from 'components';

const statusOptions = [
  {value: 'pending', label: 'Pending'},
  {value: 'allow', label: 'Allow'},
  {value: 'block', label: 'Block'},
  {value: 'review', label: 'Review'},
];

const statusFilter = {
  key: 'status',
  label: 'Status',
  type: 'select',
  options: statusOptions,
  multiSelect: false,
};

function FlowsAnalysis() {
  const reportRef = useRef(null);
  const [showFilter, setShowFilter] = useState(false);
  const [filterParams, setFilterParams] = useState({
    status: undefined,
    dateRange: undefined,
  });

  const handleFilterChange = values => {
    setFilterParams({
      status: values.status,
      dateRange: values.dateRange,
    });
  };

  const queryParams = {
    status: filterParams.status,
    ...(filterParams.dateRange && {
      date_from: filterParams.dateRange.start,
      date_to: filterParams.dateRange.end,
    }),
  };

  const {
    TOTAL_EVENTS,
    TOTAL_BLOCKED,
    TOTAL_ALLOWED,
    TOTAL_PENDING,
    totalCases,
    resolvedCases,
    unresolvedCases,
    RESOLVED_ALLOWED,
    RESOLVED_BLOCKED,
    totalResolved,
    isLoading,
  } = useFlowOverview({filters: queryParams, enabled: true});

  const resolvedPercentage = useMemo(() => {
    if (totalCases === 0) return 0;
    return Math.round((resolvedCases / totalCases) * 100);
  }, [resolvedCases, totalCases]);

  const unresolvedPercentage = useMemo(() => {
    if (totalCases === 0) return 0;
    return Math.round((unresolvedCases / totalCases) * 100);
  }, [unresolvedCases, totalCases]);

  const allowedPercentage = useMemo(() => {
    if (totalResolved === 0) return 0;
    return Math.round((RESOLVED_ALLOWED?.count / totalResolved) * 100);
  }, [RESOLVED_ALLOWED, totalResolved]);

  const blockedPercentage = useMemo(() => {
    if (totalResolved === 0) return 0;
    return Math.round((RESOLVED_BLOCKED?.count / totalResolved) * 100);
  }, [RESOLVED_BLOCKED, totalResolved]);

  const toggleFilterVisibility = () => {
    setShowFilter(prevShowFilter => !prevShowFilter);
  };

  return (
    <Layout>
      <div style={{position: 'absolute', left: '-9999px', top: '-9999px'}}>
        <FlowOverviewReport ref={reportRef} accountData={''} />
      </div>
      <div className="flex items-center py-2 flex-wrap gap-3 justify-end mx-[-32px] px-8 border border-transparent border-b-[#E4E4E6]">
        <SecondaryButton buttonText="Filter" onClick={toggleFilterVisibility} />
      </div>
      <div className={`filter-slide-container ${showFilter ? 'open' : ''}`}>
        <FilterV2
          filters={[statusFilter]}
          onFilterChange={handleFilterChange}
        />
      </div>
      <div className="flex flex-col lg:flex-row items-center gap-2 mt-2">
        <CardSummary
          className="w-full"
          title="Total Events"
          body={TOTAL_EVENTS?.count}
          loading={isLoading}
          count={TOTAL_EVENTS?.count}
        />
        <CardSummary
          className="w-full"
          title="Pending Events"
          body={TOTAL_PENDING?.count}
          loading={isLoading}
        />
        <CardSummary
          className="w-full"
          title="Allowed Events"
          body={TOTAL_ALLOWED?.count}
          loading={isLoading}
          count={TOTAL_ALLOWED?.count}
        />
        <CardSummary
          className="w-full"
          title="Blocked Events"
          body={TOTAL_BLOCKED?.count}
          loading={isLoading}
          count={TOTAL_BLOCKED?.count}
        />
      </div>

      <div className="flex items-center flex-col md:flex-row gap-4 md:h-[356px] mt-4">
        <div className="bg-white shadow-cards w-full md:w-[50%] h-full rounded-xl border-[0.5px] border-[#EFEFEF]">
          <div className="bg-[#F9F9F9] py-3 px-6 rounded-t-xl">
            <p className="font-medium text-sm xl:text-base text-deepBlue">
              Cases
            </p>
          </div>
          <div className="flex justify-between h-[87%]">
            <div className="w-[50%] flex items-center justify-center">
              <DashboardPieChartLeft
                allowedPercentage={resolvedPercentage}
                blockedPercentage={unresolvedPercentage}
                total={totalCases}
                loading={isLoading}
              />
            </div>

            <div className="w-[50%] px-6 border border-transparent border-l-[#F2F2F2] flex items-center justify-center flex-col gap-4  ">
              <div className="flex items-center gap-2 w-full justify-between">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-status-400" />
                  <p className="text-xs font-medium text-deepBlue">
                    Resolved cases
                  </p>
                </div>
                <p className="text-sm font-medium text-[#858DAA]">
                  {isLoading ? '0%' : `${resolvedPercentage}%`}
                </p>
              </div>
              <div className="flex items-center gap-2 w-full justify-between">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-[#3F7CDB]" />
                  <p className="text-xs font-medium text-deepBlue">
                    Unresolved cases
                  </p>
                </div>
                <p className="text-sm font-medium text-[#858DAA]">
                  {isLoading ? '0%' : `${unresolvedPercentage}%`}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white shadow-cards w-full md:w-[50%] h-full rounded-xl border-[0.5px] border-[#EFEFEF]">
          <div className="bg-[#F9F9F9] py-3 px-6 rounded-t-xl">
            <p className="font-medium text-sm xl:text-base text-deepBlue">
              Resolved cases
            </p>
          </div>
          <div className="flex justify-between h-[87%]">
            <div className="w-[50%] flex items-center justify-center">
              <DashboardPieChartRight
                flaggedPercentage={allowedPercentage}
                nonFlaggedPercentage={blockedPercentage}
                flagged={totalResolved}
                loading={isLoading}
              />
            </div>

            <div className="w-[50%] px-6 border border-transparent border-l-[#F2F2F2] flex items-center justify-center flex-col gap-4  ">
              <div className="flex items-center gap-2 w-full justify-between">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-status-404" />
                  <p className="text-xs font-medium text-deepBlue">
                    Allowed cases
                  </p>
                </div>
                <p className="text-sm font-medium text-[#858DAA]">
                  {isLoading ? '0%' : `${allowedPercentage}%`}
                </p>
              </div>
              <div className="flex items-center gap-2 w-full justify-between">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-red" />
                  <p className="text-xs font-medium text-deepBlue">
                    Blocked cases
                  </p>
                </div>
                <p className="text-sm font-medium text-[#858DAA]">
                  {isLoading ? '0%' : `${blockedPercentage}%`}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default connect(state => state, Actions)(FlowsAnalysis);
